import { NavList, NavListItem, NavListItemProps, Space } from '@billy/design-system'
import { Fragment, MouseEvent, useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { useUmbrella } from '@modules-deprecated/app/umbrellas'
import { useCurrentUser } from '@modules-deprecated/app/user'
import { routeToUmbrella } from '@views/umbrella/utils/routeToUmbrella'

import { CustomEvent } from '../../../../enums/customEvent'
import { EmberRoute } from '../../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useCustomEventListener } from '../../../../hooks/useCustomEventListener'
import { hashRouterLinkSecondClickFix } from '../../../../utils/hashRouterLinkSecondClickFix'
import { useNavigation } from '../../contexts/navigationContext'
import { NavigationVariant } from '../../types/navigationVariant'
import { toNavItemVariant } from '../../utils/toNavItemVariant'
import { MenuActions } from '../MenuActions'
import { NavigationMenuSkeleton } from './elements/NavigationMenuSkeleton'
import { useNavigationMenuItems } from './hooks/useNavigationMenuItems'
import * as Styled from './styles'
import { getSectionStartIds } from './utils/getSectionStartIds'
import { getSelectedMenuItem } from './utils/getSelectedMenuItem'
import { hasSubItems } from './utils/hasSubItems'
import { isNavigationMenuId } from './utils/isNavigationMenuId'
import { removeHashAndQueryParams } from './utils/removeHashAndQueryParams'

interface NavigationMenuProps {
  variant?: NavigationVariant
}

export const NavigationMenu = ({ variant = 'default' }: NavigationMenuProps) => {
  const location = useLocation()

  const [manuallyExpandedItems, setManuallyExpandedItems] = useState<string[]>([])
  // Umbrella's content (umbrella-module) runs its own, separate HashRouter/history object, so
  // navigating inside it (menu click, in-page link, or a sub-navigation tab) never fires popstate
  // on the shared history this component's own `location` is derived from - `location.hash` above
  // goes stale. HashRouterLocationChanged (dispatched by withRouteLinkClickedListener from inside
  // that router) is what tells us to re-read the address bar's actual, current hash below.
  const [hashRouterLocationTick, setHashRouterLocationTick] = useState(0)

  useCustomEventListener(CustomEvent.HashRouterLocationChanged, () => {
    setHashRouterLocationTick((tick) => tick + 1)
  })

  const { isLoading: isLoadingBookkeepingBankAccounts } = useBookkeepingBankAccounts()
  const { user } = useCurrentUser()
  const { umbrella } = useUmbrella()
  const { isNavigationOpen } = useNavigation()
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()
  const { navigationMenuItems } = useNavigationMenuItems()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const isLoading = !user || (!organizationSettings && !umbrella) || isLoadingBookkeepingBankAccounts || !generateUrl
  const shouldRenderNavList = !isLoading && !organization?.isLocked && !!(organization || umbrella)

  const currentPath = useMemo(() => {
    if (umbrella) {
      return `${location.pathname}#${removeHashAndQueryParams(window.location.hash)}`
    }

    return location.pathname
    // hashRouterLocationTick is a signal, not a value - window.location.hash is read fresh above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.pathname, umbrella, hashRouterLocationTick])

  const { selectedId, parentId } = getSelectedMenuItem({
    items: navigationMenuItems,
    organizationUrl: organization?.url,
    currentPathname: currentPath,
  })

  const navItemVariant = toNavItemVariant(variant)

  const themedMenuItems = useMemo(
    () =>
      navItemVariant
        ? navigationMenuItems.map((item) => ({
            ...item,
            variant: navItemVariant,
            subItems: item.subItems?.map((sub) => ({ ...sub, variant: navItemVariant })),
          }))
        : navigationMenuItems,
    [navigationMenuItems, navItemVariant],
  )

  const sectionStartIds = useMemo(() => getSectionStartIds(themedMenuItems), [themedMenuItems])

  const handleItemClick = useCallback(
    (id: string, value: EmberRoute | string, event?: MouseEvent) => {
      // Find the item to check if it has sub-items
      const clickedItem = themedMenuItems.find((item) => item.id === id)
      const itemHasSubItems = clickedItem && hasSubItems(clickedItem)

      // Check if the item is actually expanded (either manually or auto-expanded)
      const isCurrentlyExpanded =
        manuallyExpandedItems.includes(id) || (manuallyExpandedItems.length === 0 && parentId === id)

      // If item has sub-items and is not expanded, expand it but don't navigate to parent
      if (itemHasSubItems && !isCurrentlyExpanded && isNavigationOpen) {
        setManuallyExpandedItems([id])
        event?.preventDefault()
        return
      }

      if (umbrella?.id) {
        routeToUmbrella(value)
        event?.preventDefault()
      } else {
        hashRouterLinkSecondClickFix(event)
      }
    },
    [umbrella?.id, themedMenuItems, manuallyExpandedItems, parentId, isNavigationOpen],
  )

  const handleItemRender = useCallback(
    (props: NavListItemProps<string>) => {
      if (isNavigationMenuId(props.item.id) && sectionStartIds.includes(props.item.id)) {
        return (
          <Fragment key={props.key}>
            <Space size="s12" />
            <NavListItem {...props} />
          </Fragment>
        )
      }

      return <NavListItem {...props} />
    },
    [sectionStartIds],
  )

  const expandedItems = useMemo(() => {
    if (manuallyExpandedItems.length > 0) {
      return manuallyExpandedItems
    }
    return parentId ? [parentId] : []
  }, [parentId, manuallyExpandedItems])

  return (
    <Styled.NavigationMenuWrapper>
      <MenuActions />
      {isLoading && <NavigationMenuSkeleton />}
      {shouldRenderNavList && (
        <NavList
          items={themedMenuItems}
          expandedIds={expandedItems}
          selectedId={selectedId}
          onItemClick={handleItemClick}
          itemRender={handleItemRender}
        />
      )}
    </Styled.NavigationMenuWrapper>
  )
}
